import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import apiService from "../services/ApiService";
import TailwindInput from "../components/Tailwind/TailwindInput";
import TailwindButton from "../components/Tailwind/TailwindButton";
import { cn } from "../lib/utils";

const Validation = ({ validation_succeeds, back_to_register }) => {
  const [expirationTime, setExpirationTime] = useState<number>(0);
  const [isNotMatched, setIsNotMatched] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [reSend, setReSend] = useState<boolean>(false);
  const [buttonReSendDisabled, setButtonReSendDisabled] = useState<boolean>(false);

  const loadExpirationTimeKey = () => {
    apiService.getExpirationTimeKey().then(async (response) => {
      if (response) {
        const expirationTimeKey = await response.json();
        setExpirationTime(expirationTimeKey);
        if (!expirationTimeKey) {
          apiService.sendEmailBackRequest();
          setIsExpired(true);
          setIsNotMatched(false);
          setReSend(false);
        }
      }
    });
  };

  useEffect(() => {
    loadExpirationTimeKey();
  }, [reSend]);

  useEffect(() => {
    if (buttonReSendDisabled) {
      const timer = setTimeout(() => {
        setButtonReSendDisabled(false);
      }, 20000);
      return () => clearTimeout(timer);
    }
    return () => null;
  }, [buttonReSendDisabled]);

  const SignupSchema = Yup.object().shape({
    confirmation_code: Yup.string().required("Confirmation code required"),
  });

  const formik = useFormik({
    initialValues: {
      confirmation_code: "",
    },
    validationSchema: SignupSchema,
    onSubmit: () => {
      setTimeout(() => {
        apiService.sendConfirmationCode(values.confirmation_code).then(async (response) => {
          if (response) {
            const value_returned = await response.json();
            if (value_returned == "Match") {
              validation_succeeds();
            } else if (value_returned == "Not Match") {
              setIsNotMatched(true);
              setIsExpired(false);
              setReSend(false);
              formik.setSubmitting(false);
            } else if (value_returned == "Expire") {
              setIsExpired(true);
              setIsNotMatched(false);
              setReSend(false);
              formik.setSubmitting(false);
            }
          }
        });
      }, 500);
    },
  });

  const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-10 pb-6 text-center">
            <img
              width="220"
              height="68"
              src="/logo-text-black.png"
              alt="Peerception"
              className="mx-auto mb-6"
            />
            <div className="size-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-[28px] text-primary">mark_email_read</span>
            </div>
            <h1 className="text-xl font-bold text-slate-900">Check your email</h1>

            {/* Status messages */}
            {!isNotMatched && !isExpired && !reSend && (
              <p className="text-sm text-slate-500 mt-2">
                A confirmation email has been sent to your email address.<br />
                The code will expire in <span className="font-semibold text-slate-700">{Math.ceil(expirationTime / 60)} minute(s)</span>.
              </p>
            )}
            {isNotMatched && (
              <div className="mt-3 flex items-center gap-2 p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200 text-left">
                <span className="material-symbols-outlined text-[18px] shrink-0">error</span>
                The confirmation code does not match.
              </div>
            )}
            {isExpired && (
              <div className="mt-3 flex items-center gap-2 p-3 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200 text-left">
                <span className="material-symbols-outlined text-[18px] shrink-0">timer_off</span>
                The confirmation code has expired. A new email has been sent.
              </div>
            )}
            {reSend && (
              <p className="text-sm text-slate-500 mt-2">
                We sent you another confirmation email.<br />
                The code will expire in <span className="font-semibold text-slate-700">{Math.ceil(expirationTime / 60)} minute(s)</span>.
              </p>
            )}
          </div>

          {/* Body */}
          <div className="px-8 pb-8">
            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <div className="flex flex-col gap-4">
                  <TailwindInput
                    fullWidth
                    label="Confirmation code"
                    placeholder="Enter your code"
                    {...getFieldProps("confirmation_code")}
                    error={touched.confirmation_code && errors.confirmation_code ? String(errors.confirmation_code) : undefined}
                  />

                  <TailwindButton
                    fullWidth
                    size="lg"
                    type="submit"
                    variant="dark"
                    icon="check"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Validating...' : 'Validate'}
                  </TailwindButton>
                </div>
              </Form>
            </FormikProvider>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center flex flex-col gap-2">
          <p className="text-sm text-slate-500">Didn't receive an email?</p>
          <button
            onClick={() => {
              apiService.sendEmailBackRequest();
              setIsExpired(false);
              setIsNotMatched(false);
              setReSend(true);
              setButtonReSendDisabled(true);
            }}
            disabled={buttonReSendDisabled}
            className={cn(
              "text-sm font-semibold transition-colors",
              buttonReSendDisabled
                ? "text-slate-300 cursor-not-allowed"
                : "text-primary hover:text-blue-700"
            )}
          >
            Resend email
          </button>
          {/* Hidden when signup is disabled (no `back_to_register` handler provided) */}
          {back_to_register && (
            <button
              onClick={() => back_to_register()}
              className="text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors"
            >
              Back to sign up
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Validation;
